import React, { useContext, useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { GlobalContext } from "../../context/globalContext";
import {
  createEvent,
  EventInput,
  EventRegistrationMode,
  getEventForManage,
  updateEvent,
} from "../../api/events";
import {
  getMyAdminEvents,
  hasFullEventAdminAccess,
  isSuperAdminRole,
} from "../../api/admin";
import LoadingPanel from "../../components/LoadingPanel/LoadingPanel";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import { getUserFacingErrorMessage } from "../../utils/userFacingError";
import EventCoverImageField from "../../components/EventCoverImageField/EventCoverImageField";
import EventPricingSection from "../../components/EventPricingSection/EventPricingSection";
import type { AdminTicketType } from "../../api/tickets";
import {
  adminSetsToDrafts,
  emptyTicketSet,
  syncEventTicketSets,
  type TicketSetInput,
} from "../../api/ticketSets";
import { deactivateConferenceCatalog } from "../../utils/conferencePricing";
import { draftsFromLegacyPricing } from "../../utils/legacyPricingToTicketSets";
import { parsePricingConfig } from "../../types/pricingConfig";
import {
  deactivateSimpleTickets,
  emptyTicketRow,
  syncTicketModeCatalog,
  ticketsToDraftRows,
  type TicketDraftRow,
} from "../../utils/ticketModePricing";
import {
  dateInputToIso,
  isMeaningfulDate,
  toDateInputValue,
} from "../../utils/eventDates";

function toDatetimeLocal(iso: string | null | undefined): string {
  if (!isMeaningfulDate(iso)) return "";
  const d = new Date(iso!);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultRegistrationOpensAt(): string {
  return dateInputToIso(toDateInputValue(new Date().toISOString()), false);
}

function defaultRegistrationClosesAt(startIso: string): string {
  return dateInputToIso(toDateInputValue(startIso), true);
}

function emptyForm(): EventInput {
  const startDate = new Date().toISOString();
  return {
    title: "",
    slug: "",
    excerpt: "",
    description: "",
    venueName: "",
    venueAddress: "",
    city: "",
    country: "",
    startDate,
    endDate: null,
    registrationOpensAt: defaultRegistrationOpensAt(),
    registrationClosesAt: defaultRegistrationClosesAt(startDate),
    coverImageUrl: "",
    websiteUrl: "",
    contactEmail: "",
    registrationMode: "conference",
    tags: [],
    status: "draft",
  };
}

export default function UserPageEventForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { currentUser } = useContext(GlobalContext);

  const [form, setForm] = useState<EventInput>(emptyForm);
  const [tagsInput, setTagsInput] = useState("");
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [canAccess, setCanAccess] = useState<boolean | null>(null);
  const [chargesFees, setChargesFees] = useState(false);
  const [ticketSets, setTicketSets] = useState<TicketSetInput[]>([
    emptyTicketSet(new Date().toISOString()),
  ]);
  const [ticketRows, setTicketRows] = useState<TicketDraftRow[]>([
    emptyTicketRow(),
  ]);
  const [existingTickets, setExistingTickets] = useState<AdminTicketType[]>([]);

  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    (async () => {
      if (isEdit && id) {
        try {
          const event = await getEventForManage(Number(id));
          if (cancelled) return;
          setForm({
            title: event.title,
            slug: event.slug,
            excerpt: event.excerpt ?? "",
            description: event.description,
            venueName: event.venueName ?? "",
            venueAddress: event.venueAddress ?? "",
            city: event.city ?? "",
            country: event.country ?? "",
            startDate: event.startDate,
            endDate: isMeaningfulDate(event.endDate) ? event.endDate : null,
            registrationOpensAt:
              event.registrationOpensAt ?? defaultRegistrationOpensAt(),
            registrationClosesAt:
              event.registrationClosesAt ??
              defaultRegistrationClosesAt(event.startDate),
            coverImageUrl: event.coverImageUrl ?? "",
            websiteUrl: event.websiteUrl ?? "",
            contactEmail: event.contactEmail ?? "",
            registrationMode: event.registrationMode,
            tags: event.tags ?? [],
            status: event.status as EventInput["status"],
          });
          setTagsInput((event.tags ?? []).join(", "));
          const tickets = event.ticketTypes ?? [];
          setExistingTickets(tickets);
          let drafts =
            event.ticketSets && event.ticketSets.length > 0
              ? adminSetsToDrafts(event.ticketSets)
              : null;
          if (!drafts) {
            // One-time draft import from legacy pricingConfig for admin editing only.
            // Live catalog/checkout never reads event.pricingConfig for prices.
            const legacy = parsePricingConfig(event.pricingConfig);
            drafts = legacy
              ? draftsFromLegacyPricing(legacy, tickets, event.startDate)
              : [emptyTicketSet(event.startDate)];
          }
          setTicketSets(drafts);
          const draftRows = ticketsToDraftRows(tickets);
          setTicketRows(draftRows.length > 0 ? draftRows : [emptyTicketRow()]);
          setChargesFees(
            event.registrationMode === "invitation"
              ? false
              : (event.ticketSets && event.ticketSets.length > 0) ||
                  tickets.some(
                    (t) => t.status === "active" && t.pricePence > 0,
                  ),
          );
          setCanAccess(true);
        } catch (err) {
          if (!cancelled) {
            setError(
              getUserFacingErrorMessage(err, "Failed to load event."),
            );
            setCanAccess(false);
          }
        } finally {
          if (!cancelled) setIsLoading(false);
        }
        return;
      }

      try {
        const { events, canPostEvents } = await getMyAdminEvents();
        const allowed =
          isSuperAdminRole(currentUser.role) ||
          hasFullEventAdminAccess(events, canPostEvents);
        if (!cancelled) {
          setCanAccess(allowed);
          setIsLoading(false);
        }
      } catch {
        if (!cancelled) {
          setCanAccess(false);
          setIsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser, id, isEdit]);

  if (!currentUser) {
    return <p>Please sign in.</p>;
  }

  if (isLoading || canAccess === null) {
    return (
      <LoadingPanel
        title={isEdit ? "Loading event" : "Preparing form"}
        subtitle="Please wait."
        className="py-12"
      />
    );
  }

  if (!canAccess) {
    return <Navigate to="/user/events" replace />;
  }

  function updateField<K extends keyof EventInput>(key: K, value: EventInput[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const coverImageUrl = form.coverImageUrl?.trim() ?? "";
    if (!coverImageUrl) {
      setError("Please upload a cover image.");
      return;
    }
    if (!form.registrationOpensAt?.trim() || !form.registrationClosesAt?.trim()) {
      setError("Please set the registration period.");
      return;
    }
    if (
      new Date(form.registrationClosesAt).getTime() <
      new Date(form.registrationOpensAt).getTime()
    ) {
      setError("Registration close date must be on or after the open date.");
      return;
    }

    setIsSaving(true);

    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    const registrationMode = form.registrationMode ?? "tickets";
    if (registrationMode === "conference" && chargesFees) {
      for (const set of ticketSets) {
        if (!set.name.trim()) {
          setError("Each ticket set needs a name.");
          setIsSaving(false);
          return;
        }
        if (set.types.every((t) => !t.typeName.trim())) {
          setError(`Ticket set "${set.name}" needs at least one ticket type.`);
          setIsSaving(false);
          return;
        }
      }
    }

    const payload: EventInput = {
      ...form,
      title: form.title.trim(),
      slug: form.slug?.trim() || undefined,
      excerpt: form.excerpt?.trim() || undefined,
      description: form.description.trim(),
      venueName: form.venueName?.trim() || undefined,
      venueAddress: form.venueAddress?.trim() || undefined,
      city: form.city?.trim() || undefined,
      country: form.country?.trim() || undefined,
      coverImageUrl,
      websiteUrl: form.websiteUrl?.trim() || null,
      contactEmail: form.contactEmail?.trim() || null,
      tags: tags.length > 0 ? tags : null,
      endDate: isMeaningfulDate(form.endDate) ? form.endDate : null,
      registrationOpensAt: form.registrationOpensAt,
      registrationClosesAt: form.registrationClosesAt,
      // Clear legacy JSON pricing so ticket_types is the only price source.
      pricingConfig:
        registrationMode === "conference" ? null : undefined,
    };

    try {
      let eventId: number;
      if (isEdit && id) {
        eventId = Number(id);
        await updateEvent(eventId, payload);
      } else {
        const created = await createEvent(payload);
        eventId = created.id;
      }

      if (registrationMode === "conference") {
        if (chargesFees) {
          const synced = await syncEventTicketSets(
            eventId,
            ticketSets.map((s, i) => ({
              ...s,
              name: s.name.trim(),
              sortOrder: i,
              types: s.types.filter((t) => t.typeName.trim()),
            })),
          );
          setTicketSets(adminSetsToDrafts(synced));
        } else {
          await deactivateConferenceCatalog(existingTickets);
        }
      } else if (registrationMode === "tickets") {
        if (chargesFees) {
          await syncTicketModeCatalog(eventId, ticketRows, existingTickets);
        } else {
          await deactivateSimpleTickets(existingTickets);
        }
      }

      navigate(isEdit ? "/user/events" : `/user/events/${eventId}/edit`);
    } catch (err) {
      setError(getUserFacingErrorMessage(err, "Could not save event."));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="min-w-0">
      <Link
        to="/user/events"
        className="text-sm text-slate-500 underline mb-4 inline-block"
      >
        ← Back to events
      </Link>
      <h2 className="font-bold text-lg text-slate-900 mb-1">
        {isEdit ? "Edit event" : "Create event"}
      </h2>
      <p className="text-sm text-slate-600 mb-6">
        Update public event details shown on the event page.
      </p>

      {error && <ErrorAlert className="mb-4">{error}</ErrorAlert>}

      <form onSubmit={handleSubmit} className="space-y-4 w-full min-w-0 max-w-2xl">
        <label className="block text-sm">
          <span className="font-medium">Title *</span>
          <input
            className="mt-1 w-full border border-slate-300 p-2 text-sm"
            value={form.title}
            onChange={(e) => updateField("title", e.target.value)}
            required
          />
        </label>

        <label className="block text-sm">
          <span className="font-medium">Slug</span>
          <input
            className="mt-1 w-full border border-slate-300 p-2 text-sm"
            value={form.slug ?? ""}
            onChange={(e) => updateField("slug", e.target.value)}
            placeholder="auto-generated from title if empty"
          />
        </label>

        <label className="block text-sm">
          <span className="font-medium">Excerpt</span>
          <textarea
            className="mt-1 w-full border border-slate-300 p-2 text-sm"
            rows={2}
            value={form.excerpt ?? ""}
            onChange={(e) => updateField("excerpt", e.target.value)}
          />
        </label>

        <label className="block text-sm">
          <span className="font-medium">Description *</span>
          <textarea
            className="mt-1 w-full border border-slate-300 p-2 text-sm"
            rows={6}
            value={form.description}
            onChange={(e) => updateField("description", e.target.value)}
            required
          />
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block text-sm">
            <span className="font-medium">Start date *</span>
            <input
              type="datetime-local"
              className="mt-1 w-full border border-slate-300 p-2 text-sm"
              value={toDatetimeLocal(form.startDate)}
              onChange={(e) =>
                updateField("startDate", new Date(e.target.value).toISOString())
              }
              required
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">End date (optional)</span>
            <input
              type="datetime-local"
              className="mt-1 w-full border border-slate-300 p-2 text-sm"
              value={toDatetimeLocal(form.endDate)}
              onChange={(e) =>
                updateField(
                  "endDate",
                  e.target.value
                    ? new Date(e.target.value).toISOString()
                    : null,
                )
              }
            />
            <span className="block text-xs text-slate-500 mt-1">
              Leave blank for a single-day event.
            </span>
          </label>
        </div>

        <div className="border border-slate-200 rounded-sm p-4 bg-slate-50">
          <p className="text-sm font-medium text-slate-900 mb-1">
            Registration period *
          </p>
          <p className="text-xs text-slate-600 mb-4">
            Controls when the event appears under upcoming vs past on the home
            page, and when visitors can register.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block text-sm">
              <span className="font-medium">Opens</span>
              <input
                type="date"
                className="mt-1 w-full border border-slate-300 p-2 text-sm"
                value={
                  form.registrationOpensAt
                    ? toDateInputValue(form.registrationOpensAt)
                    : ""
                }
                onChange={(e) =>
                  updateField(
                    "registrationOpensAt",
                    e.target.value
                      ? dateInputToIso(e.target.value, false)
                      : "",
                  )
                }
                required
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium">Closes</span>
              <input
                type="date"
                className="mt-1 w-full border border-slate-300 p-2 text-sm"
                value={
                  form.registrationClosesAt
                    ? toDateInputValue(form.registrationClosesAt)
                    : ""
                }
                onChange={(e) =>
                  updateField(
                    "registrationClosesAt",
                    e.target.value
                      ? dateInputToIso(e.target.value, true)
                      : "",
                  )
                }
                required
              />
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block text-sm">
            <span className="font-medium">Venue name</span>
            <input
              className="mt-1 w-full border border-slate-300 p-2 text-sm"
              value={form.venueName ?? ""}
              onChange={(e) => updateField("venueName", e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Venue address</span>
            <input
              className="mt-1 w-full border border-slate-300 p-2 text-sm"
              value={form.venueAddress ?? ""}
              onChange={(e) => updateField("venueAddress", e.target.value)}
            />
          </label>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block text-sm">
            <span className="font-medium">City</span>
            <input
              className="mt-1 w-full border border-slate-300 p-2 text-sm"
              value={form.city ?? ""}
              onChange={(e) => updateField("city", e.target.value)}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium">Country</span>
            <input
              className="mt-1 w-full border border-slate-300 p-2 text-sm"
              value={form.country ?? ""}
              onChange={(e) => updateField("country", e.target.value)}
            />
          </label>
        </div>

        <label className="block text-sm">
          <span className="font-medium">Website URL</span>
          <input
            type="url"
            className="mt-1 w-full border border-slate-300 p-2 text-sm"
            value={form.websiteUrl ?? ""}
            onChange={(e) => updateField("websiteUrl", e.target.value)}
          />
        </label>

        <label className="block text-sm">
          <span className="font-medium">Contact email</span>
          <input
            type="email"
            className="mt-1 w-full border border-slate-300 p-2 text-sm"
            value={form.contactEmail ?? ""}
            onChange={(e) => updateField("contactEmail", e.target.value)}
          />
        </label>

        <EventCoverImageField
          value={form.coverImageUrl ?? ""}
          onChange={(url) => updateField("coverImageUrl", url)}
          eventId={isEdit && id ? Number(id) : undefined}
          eventSlug={form.slug?.trim() || undefined}
          required
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block text-sm">
            <span className="font-medium">Registration mode</span>
            <select
              className="mt-1 w-full border border-slate-300 p-2 text-sm"
              value={form.registrationMode}
              onChange={(e) =>
                updateField(
                  "registrationMode",
                  e.target.value as EventRegistrationMode,
                )
              }
            >
              <option value="conference">Conference</option>
              <option value="tickets">Tickets</option>
              <option value="invitation">Invitation</option>
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium">Status</span>
            <select
              className="mt-1 w-full border border-slate-300 p-2 text-sm"
              value={form.status}
              onChange={(e) =>
                updateField(
                  "status",
                  e.target.value as EventInput["status"],
                )
              }
            >
              <option value="draft">Draft (hidden)</option>
              <option value="published">Published (visible)</option>
              <option value="archived">Archived (hidden)</option>
            </select>
            <span className="block text-xs text-slate-500 mt-1">
              Only published events appear on the home page and event listings.
            </span>
          </label>
        </div>

        <label className="block text-sm">
          <span className="font-medium">Tags (comma-separated)</span>
          <input
            className="mt-1 w-full border border-slate-300 p-2 text-sm"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            placeholder="Technology, Networking"
          />
        </label>

        <EventPricingSection
          registrationMode={form.registrationMode ?? "tickets"}
          chargesFees={chargesFees}
          onChargesFeesChange={setChargesFees}
          ticketSets={ticketSets}
          onTicketSetsChange={setTicketSets}
          ticketRows={ticketRows}
          onTicketRowsChange={setTicketRows}
          eventStartDate={form.startDate}
        />

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            className="eh-btn-primary px-6 py-2 text-sm"
            disabled={isSaving}
          >
            {isSaving ? "Saving…" : isEdit ? "Save changes" : "Create event"}
          </button>
          <Link to="/user/events" className="eh-btn-secondary px-6 py-2 text-sm">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
